import React, { useState, useEffect } from "react";
import {
  AppBar,
  Typography,
  Button,
  IconButton,
  TextField,
  Box,
  Breadcrumbs,
  Autocomplete,
  InputAdornment,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { Packagedata } from "../../components/grid/GridData.tsx";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import { SETTING_API_06, ITEM_API_01 } from "../../api/ApiModel.tsx";
import ViewDataGrid from "../../components/grid/ViewDataGrid.tsx";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { CON_STATUS_ACTIVE, CON_STATUS_DELETED } from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import {
  PackageMessages,
  MessageCategory,
  UITextMessages,
} from "../../utils/sys/Messages.tsx";

// Types and Interfaces
interface Package {
  packagecode?: string | number;
  packagename?: string;
  descrip?: string;
  [key: string]: unknown;
}

interface Item {
  itemcode: number;
  itemname: string;
  packagecode: number;
  packagename: string;
  [key: string]: any;
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  COMPCODE?: string;
  USERNAME?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  onClick: (id: string | number) => void;
}

interface ApiError {
  response?: {
    status?: number;
    data?: string | { message?: string; error?: string };
  };
  request?: unknown;
  message?: string;
}

interface ErrorState {
  [key: string]: string | undefined;
}

interface DeletePayload {
  COMPCODE: string;
  LOCCODE: string;
  packagecode: string | number;
  packagename: string;
  descrip?: string;
  enuser: string;
  sts: string;
  MODE: string;
}

interface ApiResponse {
  success?: boolean;
  message?: string;
}

interface PageData {
  title: string;
  [key: string]: unknown;
}

interface Column {
  field: string;
  headerName: string;
  width?: number;
  flex?: number;
}

interface RowWithId extends Package {
  id: string | number;
}

function ViewPackage(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();
  
  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { LOCCODE, STAFFMAPLOCODE, COMPCODE, USERNAME } =
    SYS_USER.USER_PARAM || {};

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCheckingUsage, setIsCheckingUsage] = useState<boolean>(false);
  const [filteredRows, setFilteredRows] = useState<Package[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [data, setData] = useState<Package[] | null>(null);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<MessageCategory.SUCCESS | MessageCategory.ERROR | MessageCategory.WARNING>(MessageCategory.SUCCESS);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const [error, setError] = useState<ErrorState>({});
  const [selectedItem, setSelectedItem] = useState<Package | null>(null);

  useEffect(() => {
    setPageData({
      title: "Package",
    } as PageData);
  }, [setPageData]);

  // Function to check if package is used in any items
  const checkPackageUsage = async (packagecode: string | number): Promise<boolean> => {
    try {
      setIsCheckingUsage(true);
      
      // Fetch items with the same packagecode
      const items = await externalgetApi(ITEM_API_01, "get", {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        STS: CON_STATUS_ACTIVE,
      });

      // Check if any items use this package
      const isUsed = items?.some((item: Item) => 
        Number(item.packagecode) === Number(packagecode)
      ) || false;

      return isUsed;
    } catch (error) {
      console.error("Error checking package usage:", error);
      return false;
    } finally {
      setIsCheckingUsage(false);
    }
  };

  const handleItemClick = async (id: string | number, actionText: string): Promise<void> => {
    const selectedItem = data?.find((item) => item.packagecode === id);
    if (!selectedItem) return;

    if (actionText === "Edit") {
      navigate("/CreatePackages", { state: { selectedItem, actionText } });
    }
    if (actionText === "Delete") {
      setSelectedId(id);
      setSelectedItem(selectedItem);
      setOpenDeleteDialog(true); // Open confirmation dialog immediately
    }
  };

  const confirmDelete = async (): Promise<void> => {
    try {
      setIsLoading(true);

      if (!selectedId || !selectedItem) throw new Error("No package selected for deletion");

      // Check if package is used in any items
      const isUsed = await checkPackageUsage(selectedId);
      
      // If package is in use, show error message and don't delete
      if (isUsed) {
        setAlertMessage(PackageMessages?.error?.deleteInUse || "Package cannot be deleted. It's already in use");
        setAlertType(MessageCategory.ERROR);
        setAlertOpen(true);
        setOpenDeleteDialog(false);
        setSelectedId(null);
        setSelectedItem(null);
        return;
      }

      // Build full delete payload
      const deletePayload: DeletePayload = {
        COMPCODE: COMPCODE || "",
        LOCCODE: STAFFMAPLOCODE || "",
        packagecode: selectedId,
        packagename: selectedItem.packagename || "",
        descrip: selectedItem.descrip || "",
        enuser: USERNAME || "Admin",
        sts: CON_STATUS_DELETED || "D",
        MODE: "DELETE",
      };

      console.log("DELETE payload:", deletePayload);
      console.log("DELETE URL:", SETTING_API_06);

      // Send delete request using POST
      const response = (await externalpostApi(SETTING_API_06, deletePayload)) as ApiResponse;

      console.log("DELETE response:", response);

      if (response && response.success !== false) {
        // Remove deleted item from state
        setData((prev) => prev?.filter((row) => row.packagecode !== selectedId) || null);
        setFilteredRows((prev) =>
          prev.filter((row) => row.packagecode !== selectedId) || []
        );

        setAlertMessage(PackageMessages?.success?.delete || "Package deleted successfully");
        setAlertType(MessageCategory.SUCCESS);
        setAlertOpen(true);
        setOpenDeleteDialog(false);
        setSelectedId(null);
        setSelectedItem(null);
      } else {
        throw new Error(response?.message || PackageMessages?.error?.deleteFailed || "Delete operation failed");
      }
    } catch (error) {
      const err = error as ApiError;
      console.error("DELETE API error:", err);

      // Error handling
      let errorMessage = PackageMessages?.error?.deleteFailed || "Failed to delete package.";

      if (err.response) {
        console.error("Response Status:", err.response.status);
        console.error("Response Data:", err.response.data);

        if (err.response.data) {
          if (typeof err.response.data === "string") {
            if (err.response.data.includes("Object reference not set to an instance of an object")) {
              errorMessage = "Null reference error occurred";
            } else if (err.response.data.trim() !== "") {
              errorMessage = err.response.data;
            }
          } else if ((err.response.data as any).message) {
            errorMessage = (err.response.data as any).message;
          } else if ((err.response.data as any).error) {
            errorMessage = (err.response.data as any).error;
          }
        }

        // Handle specific HTTP status codes
        switch (err.response.status) {
          case 400:
            errorMessage = errorMessage || "Bad request";
            break;
          case 404:
            errorMessage = "Resource not found";
            break;
          case 405:
            errorMessage = "Method not allowed";
            break;
          case 500:
            errorMessage = errorMessage || "Server error";
            break;
          default:
            errorMessage = `${errorMessage} (Status: ${err.response.status})`;
        }
      } else if (err.request) {
        errorMessage = "No response from server";
      } else {
        errorMessage = err.message || errorMessage;
      }

      setAlertMessage(errorMessage);
      setAlertType(MessageCategory.ERROR);
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelDelete = (): void => {
    setOpenDeleteDialog(false);
    setSelectedId(null);
    setSelectedItem(null);
  };

  const menuItems: MenuItem[] = [
    {
      text: "Edit",
      icon: <BorderColorOutlinedIcon />,
      onClick: (packagecode: string | number) => handleItemClick(packagecode, "Edit"),
    },
    {
      text: "Delete",
      icon: <DeleteIcon />,
      onClick: (packagecode: string | number) => handleItemClick(packagecode, "Delete"),
    },
  ];

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const result = (await externalgetApi(SETTING_API_06, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        })) as Package[];

        setData(result);
        setFilteredRows(result);
        console.log("result", result);
        setIsLoading(false);
      } catch (err) {
        setError(err as ErrorState);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [COMPCODE, STAFFMAPLOCODE]);

  // Search handler
  const handleSearchChange = (event: React.SyntheticEvent, value: string | null): void => {
    const searchValue = value || "";
    setSearchQuery(searchValue);
    
    if (!searchValue.trim()) {
      setFilteredRows(data || []);
      return;
    }

    const q = searchValue.toLowerCase().trim();
    
    const filtered = data?.filter((row) => {
      const packagecodeStr = row.packagecode?.toString() || "";
      
      return (
        row.packagename?.toLowerCase().includes(q) ||
        packagecodeStr.toLowerCase().includes(q)
      );
    });
    
    setFilteredRows(filtered || []);
  };

  const addToView = (): void => {
    navigate("/CreatePackages");
  };

  // Prepare rows with id for the grid
  const rowsWithId: RowWithId[] = filteredRows.map((row) => ({
    ...row,
    id: row.packagecode || Math.random(),
  }));

  // Placeholder processRowUpdate function
  const processRowUpdate = async (newRow: any): Promise<any> => {
    console.log("Row update attempted:", newRow);
    return newRow;
  };

  return (
    <>
      <Box sx={styles.box_breadcrumb_view}>
        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
          >
            {UITextMessages?.breadcrumb?.home || "Home"}
          </Link>
          <Link
            sx={styles.breadcrumb_link}
            underline="hover"
          >
            {UITextMessages?.breadcrumb?.settings || "Settings"}
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            aria-current="page"
          >
            Package
          </Link>
        </Breadcrumbs>
      </Box>

      <Box sx={styles.box_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 8, xl: 8 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>
                {Packagedata.name}
              </Typography>
            </Grid>

            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}
              sx={styles.appbar_grid_search}
            >
              <Autocomplete
                freeSolo
                id="act_appbarsearch"
                fullWidth
                disableClearable
                value={searchQuery}
                onInputChange={handleSearchChange}
                sx={styles.ipt_appbar_search_auto}
                options={data?.map((row) => row.packagename || "").filter(Boolean) || []}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_appbarsearch"
                      {...restParams}
                      placeholder={PackageMessages?.placeholder?.search || "Search by..."}
                      size="small"
                      sx={styles.ipt_appbar_search}
                      InputProps={{
                        ...restParams.InputProps,
                        type: "search",
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              id="btn_search"
                              sx={styles.btn_appbar_search_icon}
                            >
                              <SearchIcon id="ico_search" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  );
                }}
              />

              <Button
                id="btn_add"
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                size="small"
                sx={{ ...styles.btn_primary, ...styles.btn_appbar_add }}
                onClick={addToView}
                disabled={isLoading}
              >
                {UITextMessages?.button?.add || "Add"}
              </Button>
            </Grid>
          </Grid>
        </AppBar>

        <Box sx={styles.view_page_container}>
          <ViewDataGrid
            columns={Packagedata.columns}
            rows={rowsWithId}
            menuItems={menuItems}
            tabletitle={Packagedata.name}
            gridid="packagecode"
            processRowUpdate={processRowUpdate}
          />
        </Box>
      </Box>

      <SystemAlert
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />

      <Dialog
        id="dlg_delete"
        open={openDeleteDialog}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedItem ? (
              `Are you sure you want to delete the package "${selectedItem.packagename}"?`
            ) : (
              "Are you sure you want to delete this package?"
            )}
          </DialogContentText>
          {/* {selectedItem?.descrip && (
            <DialogContentText sx={{ mt: 2, fontStyle: 'italic' }}>
              Description: {selectedItem.descrip}
            </DialogContentText>
          )} */}
        </DialogContent>
        <DialogActions>
          <Button
            id="btn_cancel"
            onClick={cancelDelete}
            variant="outlined"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            disabled={isLoading || isCheckingUsage}
          >
            {UITextMessages?.button?.cancel || "Cancel"}
          </Button>
          <Button
            id="btn_delete"
            onClick={confirmDelete}
            color="error"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            variant="contained"
            disabled={isLoading || isCheckingUsage}
          >
            {isLoading ? "Deleting..." : (UITextMessages?.button?.delete || "Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ViewPackage;